import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { TopBar } from './TopBar';
import { zAuthActionQuery, zPortalQuery } from './portalTypes';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SignIn } from '../auth/SignIn';
import { z } from 'zod';
import { ChildrenProps, StyleProps } from 'shared/ui/uiType';
import { compactString } from 'shared/text';
import { delay, doTo, okOrThrow } from 'shared/promise';
import { notNullOrEmpty } from 'shared/collection';
import { MentionContext } from 'components/form/lexical/MentionNode';
import { P, match } from 'ts-pattern';
import { Alert, Button, Loader, ScrollArea, Tooltip } from '@mantine/core';
import { PortalContext, usePortalLoad } from './portal';
import { useQueryString } from 'utils/useQueryString';
import { RichText } from 'components/form/RichText';
import { showError } from 'components/notify';
import { useRouter } from 'next/router';
import { showNotification } from '@mantine/notifications';
import { Workspace as W } from './workspace';
import { sb } from 'utils/db';
import { tourCfg, TourId, zTourSettings } from './tourTypes';
import { Tour, TourContext, useTour } from './Tour';
import { useForm } from '@mantine/form';
import { ChangePasswordForm } from 'components/auth/ChangePasswordForm';
import { HiOutlineLogout } from 'react-icons/hi';
import { PrePortalContainer } from './PrePortalContainer';
import { Notify } from 'api/notify';
const zRequestMy = z.object({
  cfg: W.zRequest,
  has_access: z.boolean()
});
export const Portal = t0 => {
  const $ = _c(49);
  const {
    children,
    className,
    noScroll,
    noTopBar
  } = t0;
  const uPortal = usePortalLoad();
  const {
    status,
    portalError,
    profile,
    logOut,
    profilesUI,
    user,
    profiles,
    updateProfileGlobal
  } = uPortal;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["sb.auth.initialize"],
      queryFn: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: authData
  } = useQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = zPortalQuery.pick({
      invite: true,
      workspace: true,
      noPrompt: true,
      emailInvite: true
    }).merge(zAuthActionQuery.pick({
      authAction: true,
      error: true,
      error_description: true
    }));
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    query,
    setQuery
  } = useQueryString(t2);
  const {
    workspace: workspace_id,
    authAction
  } = query;
  const roles = profile?.roles ?? [];
  const router = useRouter();
  const [requestAccessState, setRequestAccess] = useState(undefined);
  const topBarRef = useRef(null);
  const authButNoAccess = status == "authenticated" && roles && roles.length == 0;
  let t3;
  if ($[2] !== workspace_id) {
    t3 = async () => await sb.from("access_request_my").select("cfg, has_access").match({
      workspace_id
    }).limit(1).maybeSingle().then(_temp3);
    $[2] = workspace_id;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const requestAccessQuery = useQuery({
    queryKey: ["access-request", workspace_id],
    queryFn: t3,
    enabled: !!workspace_id && authButNoAccess,
    refetchInterval: 5000
  });
  let t4;
  if ($[4] !== requestAccessState) {
    t4 = () => requestAccessState;
    $[4] = requestAccessState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const requestAccess = match(requestAccessQuery.data?.status).with("ignore", "requested", _temp4).otherwise(t4);
  let t5;
  if ($[6] !== requestAccessQuery.data || $[7] !== requestAccessState || $[8] !== router) {
    t5 = () => {
      const d = requestAccessQuery.data;
      if (d && d.status == "add" && d.has_access && requestAccessState == "request-sent") {
        showNotification({
          message: "Request accepted, reloading"
        });
        delay(2000).then(() => router.reload());
      }
    };
    $[6] = requestAccessQuery.data;
    $[7] = requestAccessState;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== requestAccessQuery.data || $[11] !== requestAccessState) {
    t6 = [requestAccessQuery.data, requestAccessState];
    $[10] = requestAccessQuery.data;
    $[11] = requestAccessState;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      initialValues: {
        message: ""
      }
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const form = useForm(t7);
  let t8;
  if ($[14] !== profiles?.global?.tour) {
    t8 = profiles?.global?.tour ?? zTourSettings.parse({});
    $[14] = profiles?.global?.tour;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const tourSettings = t8;
  let t10;
  let t9;
  if ($[16] !== tourSettings || $[17] !== updateProfileGlobal) {
    t9 = (id, mode) => {
      updateProfileGlobal({
        tour: {
          ...tourSettings,
          seen: mode == "un-see" ? tourSettings.seen.filter(s => s != id) : [...tourSettings.seen, id]
        }
      });
    };
    t10 = async () => {
      updateProfileGlobal({
        tour: {
          ...tourSettings,
          noTips: true
        }
      });
    };
    $[16] = tourSettings;
    $[17] = updateProfileGlobal;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== updateProfileGlobal) {
    t11 = () => {
      updateProfileGlobal({
        tour: zTourSettings.parse({})
      });
    };
    $[20] = updateProfileGlobal;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9 || $[25] !== tourSettings || $[26] !== uPortal.noPrompt) {
    t12 = {
      disabled: uPortal.noPrompt,
      settings: tourSettings,
      onSeen: t9,
      onDisable: t10,
      onReset: t11
    };
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = tourSettings;
    $[26] = uPortal.noPrompt;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const tour = useTour(tourCfg, t12);
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mutationFn: _temp6
    };
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const addMyself = useMutation(t13);
  let t14;
  if ($[29] !== user?.email) {
    t14 = async t15 => {
      const {
        message,
        workspace_id: workspace_id_1
      } = t15;
      const {
        error: error_1
      } = await sb.rpc("access_request", {
        message,
        workspace_id: workspace_id_1
      });
      if (error_1) {
        throw error_1;
      }
      return await Notify.notifyOnAuthRequest({
        workspace_id: workspace_id_1,
        email: user?.email ?? ""
      }).then(okOrThrow);
    };
    $[29] = user?.email;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setRequestAccess("request-sent");
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14) {
    t16 = {
      mutationFn: t14,
      onSuccess: t15
    };
    $[32] = t14;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const requestAccessMutation = useMutation(t16);
  let t17;
  if ($[34] !== authData?.error?.message || $[35] !== portalError || $[36] !== query.emailInvite || $[37] !== query.error || $[38] !== query.error_description || $[39] !== query.invite || $[40] !== uPortal) {
    t17 = () => {
      const isInvite = query?.invite != null || query?.emailInvite != null;
      return <><SignIn initialMode={isInvite ? "signup" : undefined} actionDesc={isInvite ? "accept your invite" : undefined} error={query?.error ? `${query?.error}: ${query?.error_description}` : authData?.error?.message ?? portalError} onSignIn={{
          action: () => uPortal.portalRefetch(),
          desc: "Loading portal"
        }} /><Alert variant="light" color="red" title="Error loading app" className="min-w-[10em]">{portalError}</Alert></>;
    };
    $[34] = authData?.error?.message;
    $[35] = portalError;
    $[36] = query.emailInvite;
    $[37] = query.error;
    $[38] = query.error_description;
    $[39] = query.invite;
    $[40] = uPortal;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== setQuery || $[43] !== user?.email) {
    t18 = () => <PrePortalContainer title="Reset password"><ChangePasswordForm email={user?.email} close={() => {
        setQuery({
          authAction: undefined
        });
      }} buttonLabel="Set password" /></PrePortalContainer>;
    $[42] = setQuery;
    $[43] = user?.email;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t21;
  if ($[45] !== portalError) {
    t21 = () => <PrePortalContainer><Alert variant="light" color="red" title="Error loading app" className="min-w-[10em]">{portalError}</Alert></PrePortalContainer>;
    $[45] = portalError;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = match({
    portalError,
    status,
    roles,
    authAction
  }).with({
    status: P.union("error", "un-authenticated")
  }, t17).with({
    status: "authenticated",
    authAction: "recovery",
    roles: []
  }, t18).with({
    status: "authenticated",
    roles: []
  }, () => <PrePortalContainer className="gap-2" title="No access"><div className="opacity-60">{user?.is_anonymous ? "You don't have access to a swift workspace." : `You are signed-in, but don't have access to ${workspace_id ? "this workspace" : "a swift workspace"}`}</div>{portalError && <Alert variant="light" color="red" className="min-w-[10em]">{portalError}</Alert>}{match({
      requestAccess,
      workspace_id,
      email: compactString(user?.email)
    }).with({
      requestAccess: "edit-message",
      workspace_id: P.not(P.nullish),
      email: P.not(P.nullish)
    }, t19 => {
      const {
        workspace_id: workspace_id_2
      } = t19;
      return <div className="w-[40em] flex flex-col gap-4"><RichText autoFocus={true} placeholder="Message to admin" {...form.getInputProps("message")} onKeyDown={(editor, e) => {
          if (e.ctrlKey && e.key == "Enter") {
            requestAccessMutation.mutate({
              message: editor.getMd(),
              workspace_id: workspace_id_2
            });
          }
        }} /><div className="flex flex-col gap-2"><Button variant="filled" className="self-end" loading={requestAccessMutation.isPending} onClick={() => {
            requestAccessMutation.mutate({
              message: form.values.message ?? "",
              workspace_id: workspace_id_2
            });
          }}>Request access</Button>{requestAccessMutation.isError && <Alert color="red">{requestAccessMutation.error.message}</Alert>}</div></div>;
    }).with({
      requestAccess: "request-sent",
      workspace_id: P.not(P.nullish),
      email: P.not(P.nullish)
    }, _temp7).otherwise(() => <div className="flex gap-6 justify-end w-full mt-6">{workspace_id && notNullOrEmpty(user?.email) && <Button variant="filled" onClick={() => setRequestAccess("edit-message")}>Request access</Button>}{workspace_id && profiles?.global?.hasRole("admin") && user?.id && <Tooltip label="You are a global admin, so you can add yourself to this workspace if needed"><Button loading={addMyself.isPending} variant="filled" color="red" onClick={() => {
          addMyself.mutate({
            user_id: user.id,
            workspace_id
          }, {
            onSuccess: () => router.reload()
          });
        }}>Force my way in</Button></Tooltip>}{user && !user.is_anonymous && <Button variant="outline" onClick={() => router.push("/new-workspace")}>Create new workspace</Button>}<Button leftSection={<HiOutlineLogout />} onClick={() => logOut()}>sign out</Button></div>)}</PrePortalContainer>).with({
    status: "authenticated"
  }, () => <div className={`grid h-[100dvh] ${noTopBar ? "w-screen" : " grid-rows-[3rem_minmax(0,_1fr)]"} ${className ? className : ""}`}><PortalContext.Provider value={uPortal as any}><MentionContext.Provider value={{
        profiles: profilesUI
      }}><TourContext.Provider value={tour}><>{!noTopBar && <TopBar ref={topBarRef} />}{noScroll ? children : <ScrollArea className="flex w-full flex-col items-center px-1 md:px-2" onScrollPositionChange={t20 => {
              const {
                y
              } = t20;
              if (topBarRef.current) {
                if (y > 0) {
                  topBarRef.current.classList.add("shadow-md");
                } else {
                  topBarRef.current.classList.remove("shadow-md");
                }
              }
            }}>{children}</ScrollArea>}<Tour tour={tour} /></></TourContext.Provider></MentionContext.Provider></PortalContext.Provider></div>).with({
    portalError: P.not(P.nullish)
  }, t21).otherwise(_temp8);
  let t23;
  if ($[47] !== t22) {
    t23 = <>{t22}</>;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};
function _temp() {
  return sb.auth.initialize();
}
function _temp2(t0) {
  const {
    cfg,
    ...r
  } = t0;
  return {
    ...r,
    ...cfg
  };
}
function _temp3(t0) {
  const {
    error,
    data
  } = t0;
  if (error) {
    showError(error);
  }
  const res = data ? doTo(zRequestMy.parse(data), _temp2) : null;
  return res;
}
function _temp4() {
  return "request-sent";
}
function _temp5(t0) {
  const {
    error: error_0
  } = t0;
  if (error_0) {
    showError(error_0);
    throw error_0;
  }
}
async function _temp6(t0) {
  const {
    user_id,
    workspace_id: workspace_id_0
  } = t0;
  return sb.rpc("profile_upsert", {
    p_user_id: user_id,
    p_workspace_id: workspace_id_0,
    p_cfg: {
      roles: ["admin"]
    }
  }).then(_temp5);
}
function _temp7(t0) {
  const {
    email: email_0
  } = t0;
  return <div className="max-w-[40em]">You have sent a request for access <span className="opacity-50">(email: {email_0})</span>. An admin can review the request and let you in. This page will automatically reload if the request is accepted.</div>;
}
function _temp8() {
  return <PrePortalContainer noOverlay={true}><Loader color="primary" variant="dots" size="xl" /></PrePortalContainer>;
}